import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2, ArrowRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { formatR } from "@/lib/api";

export function CartDrawer() {
  const { drawerOpen, setDrawerOpen, items, setQty, remove, subtotal, settings } = useCart();
  const navigate = useNavigate();
  const freeOver = settings.free_delivery_over || 750;
  const progress = Math.min(100, (subtotal / freeOver) * 100);

  return (
    <AnimatePresence>
      {drawerOpen && (
        <>
          <motion.div
            className="fixed inset-0 bg-navy-dark/60 backdrop-blur-sm z-[60]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setDrawerOpen(false)}
            data-testid="cart-drawer-overlay"
          />
          <motion.aside
            className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white z-[70] flex flex-col shadow-2xl"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
              <h3 className="font-display font-bold text-lg text-navy">Your cart</h3>
              <button
                onClick={() => setDrawerOpen(false)}
                className="p-2 rounded-full hover:bg-slate-100 transition-colors"
                data-testid="cart-drawer-close"
                aria-label="Close cart"
              >
                <X size={18} />
              </button>
            </div>
            <div className="px-6 py-4 bg-cream border-b border-slate-100">
              <p className="text-xs font-medium text-slate-600 mb-2" data-testid="free-shipping-note">
                {subtotal >= freeOver
                  ? "You've unlocked FREE delivery"
                  : `Add ${formatR(freeOver - subtotal)} more for free delivery`}
              </p>
              <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden">
                <motion.div
                  className="h-full bg-gold rounded-full"
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {items.length === 0 && (
                <p className="text-sm text-slate-500 py-10 text-center" data-testid="cart-empty">
                  Your cart is empty. Let's fix that.
                </p>
              )}
              {items.map((i) => {
                const k = i.key || i.id;
                return (
                <div key={k} className="flex gap-3" data-testid={`cart-item-${k.replace("|", "-")}`}>
                  <img src={i.image} alt={i.name} className="w-16 h-16 rounded-xl object-cover border border-slate-100" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-navy truncate">{i.name}</p>
                    {i.variant && (
                      <p className="text-[11px] font-bold text-brand mt-0.5" data-testid={`cart-variant-${k.replace("|", "-")}`}>{i.variant_label || "Option"}: {i.variant}</p>
                    )}
                    <p className="text-xs text-slate-500 font-mono">{formatR(i.price)} each</p>
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => setQty(k, i.qty - 1)}
                        className="w-7 h-7 rounded-lg border border-slate-200 flex items-center justify-center hover:border-gold transition-colors"
                        data-testid={`cart-minus-${k.replace("|", "-")}`}
                        disabled={i.qty <= 1}
                      >
                        <Minus size={13} />
                      </button>
                      <span className="text-sm font-bold w-6 text-center" data-testid={`cart-qty-${k.replace("|", "-")}`}>{i.qty}</span>
                      <button
                        onClick={() => setQty(k, i.qty + 1)}
                        className="w-7 h-7 rounded-lg border border-slate-200 flex items-center justify-center hover:border-gold transition-colors"
                        data-testid={`cart-plus-${k.replace("|", "-")}`}
                      >
                        <Plus size={13} />
                      </button>
                      <button
                        onClick={() => remove(k)}
                        className="ml-auto p-1.5 text-slate-400 hover:text-red-500 transition-colors"
                        data-testid={`cart-remove-${k.replace("|", "-")}`}
                        aria-label="Remove"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                  <p className="text-sm font-bold text-navy font-mono">{formatR(i.price * i.qty)}</p>
                </div>
                );
              })}
            </div>
            <div className="border-t border-slate-100 px-6 py-5 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Subtotal</span>
                <span className="font-bold text-navy font-mono" data-testid="cart-subtotal">{formatR(subtotal)}</span>
              </div>
              <button
                onClick={() => {
                  setDrawerOpen(false);
                  navigate("/checkout");
                }}
                disabled={items.length === 0}
                className="w-full h-12 rounded-full bg-gold hover:bg-gold-hover disabled:opacity-40 text-white font-bold text-sm flex items-center justify-center gap-2 transition-colors gold-glow"
                data-testid="cart-checkout-button"
              >
                Checkout securely <ArrowRight size={16} />
              </button>
              <Link
                to="/cart"
                onClick={() => setDrawerOpen(false)}
                className="block text-center text-xs font-semibold text-navy hover:text-brand transition-colors"
                data-testid="cart-view-page-link"
              >
                View full cart
              </Link>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
